import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ActivityIndicator, ScrollView, StyleSheet, View, type StyleProp, type ViewStyle } from "react-native";
import { z } from "zod";
import { StoragePathType } from "@axio/graphql/generated/graphql";

import CustomAdminModal from "../../common/general/CustomAdminModal/CustomAdminModal";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import CustomText from "../../common/general/CustomText/CustomText";
import CustomTextInput from "../../common/general/CustomTextInput/CustomTextInput";
import ImageUpload from "../../common/general/ImageUpload/ImageUpload";
import { colors } from "../../../styles/design-system";

const moduleFormSchema = z.object({
  title: z.string().trim().min(1, "Module name is required"),
  description: z.string().trim().optional(),
  thumbnail: z.string().optional(),
});

type ModuleFormValues = z.infer<typeof moduleFormSchema>;

export type ModuleFormSubmitInput = {
  title: string;
  description?: string;
  thumbnail?: string;
};

type ModuleFormInitialValues = Partial<ModuleFormSubmitInput>;

type ModuleFormModalProps = {
  visible: boolean;
  title: string;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (input: ModuleFormSubmitInput) => void | Promise<void>;
  initialValues?: ModuleFormInitialValues;
  showThumbnail?: boolean;
  style?: StyleProp<ViewStyle>;
};

const emptyModuleFormValues: ModuleFormValues = {
  title: "",
  description: "",
  thumbnail: "",
};

function getFormValues(initialValues?: ModuleFormInitialValues): ModuleFormValues {
  return {
    title: initialValues?.title ?? "",
    description: initialValues?.description ?? "",
    thumbnail: initialValues?.thumbnail ?? "",
  };
}

function getSubmitInput(values: ModuleFormValues, includeThumbnail: boolean): ModuleFormSubmitInput {
  const description = values.description?.trim();
  const thumbnail = values.thumbnail?.trim();
  const input: ModuleFormSubmitInput = {
    title: values.title.trim(),
  };

  if (description) {
    input.description = description;
  }

  if (includeThumbnail && thumbnail) {
    input.thumbnail = thumbnail;
  }

  return input;
}

function ModuleFormModal({
  visible,
  title,
  isSubmitting,
  onClose,
  onSubmit,
  initialValues,
  showThumbnail = false,
  style,
}: ModuleFormModalProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ModuleFormValues>({
    resolver: zodResolver(moduleFormSchema),
    defaultValues: emptyModuleFormValues,
    mode: "onChange",
  });

  useEffect(() => {
    reset(visible ? getFormValues(initialValues) : emptyModuleFormValues);
    setUploadError(null);
  }, [initialValues, reset, visible]);

  const moduleTitle = watch("title");
  const isSaveDisabled = !moduleTitle?.trim() || isSubmitting;
  const handleFormSubmit = handleSubmit((values) => onSubmit(getSubmitInput(values, showThumbnail)));

  return (
    <CustomAdminModal visible={visible} onRequestClose={onClose} title={title} ariaLabel={title} style={style}>
      <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} tabIndex={0}>
        <View style={styles.fieldGroup}>
          <CustomText text="Module Name" useTranslationText={false} size="s" weight="bold" style={styles.label} />
          <Controller
            control={control}
            name="title"
            render={({ field: { value, onChange } }) => (
              <CustomTextInput
                value={value ?? ""}
                placeholder="Enter module name"
                onChangeText={onChange}
                accessibilityLabel="Module name"
                editable={!isSubmitting}
                error={errors.title?.message}
                useTranslationText={false}
                style={styles.input}
              />
            )}
          />
        </View>

        <View style={styles.fieldGroup}>
          <View style={styles.labelRow}>
            <CustomText
              text="Module Description"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.label}
            />
            <CustomText text="(optional)" useTranslationText={false} size="s" style={styles.optionalLabel} />
          </View>
          <Controller
            control={control}
            name="description"
            render={({ field: { value, onChange } }) => (
              <CustomTextInput
                value={value ?? ""}
                placeholder="Enter module description (optional)"
                onChangeText={onChange}
                accessibilityLabel="Module description"
                editable={!isSubmitting}
                multiline
                numberOfLines={4}
                useTranslationText={false}
                style={[styles.input, styles.textArea]}
              />
            )}
          />
          {errors.description?.message ? (
            <CustomText
              text={errors.description.message}
              size="xs"
              style={styles.errorText}
              useTranslationText={false}
            />
          ) : null}
        </View>

        {showThumbnail ? (
          <View style={styles.fieldGroup}>
            <CustomText
              text="Upload Thumbnail (Optional)"
              useTranslationText={false}
              size="s"
              weight="bold"
              style={styles.label}
            />
            <Controller
              control={control}
              name="thumbnail"
              render={({ field: { value, onChange } }) => (
                <ImageUpload
                  value={value ?? ""}
                  onChange={onChange}
                  storagePath={StoragePathType.Thumbnails}
                  containerStyle={styles.thumbnailUpload}
                  imageStyle={styles.thumbnailImage}
                  aspectRatio={[16, 9]}
                  allowsEditing
                  buttonText="Upload Thumbnail"
                  changeButtonText="Change Thumbnail"
                  dropzoneText="Drag & drop module thumbnail here"
                  dropzoneSubtext=""
                  onError={setUploadError}
                />
              )}
            />
            {uploadError ? (
              <CustomText text={uploadError} size="xs" style={styles.errorText} useTranslationText={false} />
            ) : null}
            <CustomText
              text="Upload an image for the module. JPG, PNG, or WebP, max 2MB."
              size="xs"
              style={styles.helperText}
              useTranslationText={false}
            />
          </View>
        ) : null}

        <View style={styles.buttonRow}>
          <CustomButton
            text="Cancel"
            onPress={onClose}
            styleType="primaryLight"
            size="m"
            disabled={isSubmitting}
            style={styles.cancelButton}
            textStyle={styles.cancelButtonText}
            useTranslationText={false}
            accessibilityLabel="Cancel module form"
          />
          <CustomButton
            text={isSubmitting ? "Saving..." : "Save"}
            onPress={handleFormSubmit}
            styleType="primary"
            style={[styles.saveButton, isSaveDisabled && styles.saveButtonDisabled]}
            size="m"
            disabled={isSaveDisabled}
            leftIcon={isSubmitting ? <ActivityIndicator size="small" color="#FFFFFF" /> : undefined}
            useTranslationText={false}
            accessibilityLabel="Save module"
          />
        </View>
      </ScrollView>
    </CustomAdminModal>
  );
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  contentInner: {
    padding: 24,
    gap: 24,
  },
  fieldGroup: {
    gap: 8,
  },
  label: {
    color: colors.grey[900],
  },
  labelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  optionalLabel: {
    color: colors.grey[400],
  },
  input: {
    minHeight: 44,
    borderColor: colors.grey[300],
    paddingHorizontal: 14,
    paddingVertical: 10,
    fontSize: 16,
  },
  textArea: {
    minHeight: 96,
    textAlignVertical: "top",
  },
  errorText: {
    color: colors.error[500],
  },
  helperText: {
    color: colors.grey[500],
  },
  thumbnailUpload: {
    width: 300,
    height: 168,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.grey[300],
    backgroundColor: colors.grey[50],
    justifyContent: "center",
    alignItems: "center",
  },
  thumbnailImage: {
    borderRadius: 8,
    overflow: "hidden",
    justifyContent: "center",
    alignItems: "center",
  },
  buttonRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 12,
  },
  cancelButton: {
    minHeight: 44,
    borderRadius: 8,
    shadowColor: "transparent",
    elevation: 0,
  },
  cancelButtonText: {
    color: colors.grey[700],
  },
  saveButton: {
    minHeight: 44,
    borderRadius: 8,
    paddingHorizontal: 18,
  },
  saveButtonDisabled: {
    backgroundColor: colors.brand[200],
  },
});

export default ModuleFormModal;
